import { ZodError } from 'zod'
import superjson from 'superjson'
import Rollbar from 'rollbar'

export type Err = ZodError | Error | any

let _rollbarInstance: Rollbar | null | undefined

/**
 * The environment label for Rollbar only — never the app's own environment.
 *
 * Netlify sets CONTEXT on every build and deploy (`production`,
 * `deploy-preview`, `branch-deploy`) while a site-level ENVIRONMENT applies to
 * all of them unless scoped per context, which is why preview builds used to
 * file into the production Rollbar project. ROLLBAR_ENVIRONMENT comes first so a
 * build can bake the answer in where CONTEXT is not in the runtime environment.
 * Render has no CONTEXT: a preview service there is marked only by IS_PULL_REQUEST.
 */
export function resolveEnvironment(): string | undefined {
  const explicit = process?.env?.ROLLBAR_ENVIRONMENT
  if (explicit) return explicit
  const context = process?.env?.CONTEXT
  if (context && context !== 'production') return context
  if (process?.env?.IS_PULL_REQUEST === 'true') return 'deploy-preview'
  // Without a label the Rollbar client files under NODE_ENV || 'development'.
  return process?.env?.ENVIRONMENT || context || undefined
}

/**
 * Netlify names the built commit COMMIT_REF, Render names it RENDER_GIT_COMMIT.
 * Rollbar needs this to apply the uploaded source maps; without it it groups on
 * the content-hashed bundle name, so one bug becomes a fresh item per deploy.
 */
export function resolveCodeVersion(): string | undefined {
  return (
    process?.env?.ROLLBAR_CODE_VERSION ||
    process?.env?.RENDER_GIT_COMMIT ||
    process?.env?.COMMIT_REF ||
    undefined
  )
}

/**
 * A dev server (`astro dev`, `nuxt dev`) runs with NODE_ENV=development and the committed
 * tokens of the live projects, so its errors would land among production's. There is no
 * opt-in: nuxt-rollbar fills ROLLBAR_ENVIRONMENT from ENVIRONMENT, so it can't be one.
 */
export function isLocalDevServer(): boolean {
  return process?.env?.NODE_ENV === 'development'
}

function rollbar(): Rollbar | null {
  if (_rollbarInstance !== undefined) return _rollbarInstance
  if (isLocalDevServer()) return (_rollbarInstance = null)
  const serverToken = process?.env?.ROLLBAR_TOKEN_SERVER || process?.env?.ROLLBAR_SERVER
  _rollbarInstance = serverToken
    ? new Rollbar({
        accessToken: serverToken,
        captureUncaught: true,
        captureUnhandledRejections: true,
        // Default is 60/min. Webhook + outbound audit logs can burst above
        // that; raise the ceiling so we don't silently drop signal under load.
        itemsPerMinute: 600,
        payload: {
          environment: resolveEnvironment(),
          code_version: resolveCodeVersion(),
        },
      })
    : null
  return _rollbarInstance
}

/**
 * Console only, no external report. Spelled out because the logger argument
 * defaults to the shared instance, so there has to be a way to opt out that
 * isn't a falsy value silently skipping the default.
 */
export const CONSOLE_ONLY = 'console-only' as const

export type ErrorSink = Rollbar | typeof CONSOLE_ONLY

function resolveSink(sink: ErrorSink | undefined): Rollbar | null {
  if (sink === CONSOLE_ONLY) return null
  return sink ?? rollbar()
}

const MAX_DESCRIPTION = 300

function stringifySafely(value: unknown): string | null {
  try {
    return superjson.stringify(value)
  } catch {
    return null
  }
}

// Rollbar groups by the Error message, so prefer the status/message pair that an
// H3 or ofetch rejection carries over serialising the whole object. Callers pass
// such objects routinely — `error.value.data` from useFetch is one — and they
// used to collapse into the single literal 'Unknown error'.
function describeUnknown(e: Err): string {
  if (e === null || e === undefined) return 'Unknown error'
  if (typeof e !== 'object') return String(e)
  const { statusCode, status, statusMessage, message } = e as Record<string, unknown>
  const code = statusCode ?? status
  // Not `??`: an empty statusMessage is present but useless, and message is
  // usually the one carrying the description.
  const text = [statusMessage, message].find(v => typeof v === 'string' && v !== '')
  if (typeof text === 'string') return code === undefined ? text : `${code} ${text}`
  return stringifySafely(e)?.slice(0, MAX_DESCRIPTION) ?? 'Unknown error'
}

/**
 * Detail the thrower has already made safe to log — a fetch client keeps a
 * failed reply's body on the error and out of its message, so without this a
 * 5xx reaches Rollbar as its status line alone. Only a string, and only one the
 * thrower put here deliberately: a raw body can hold personal data, so
 * redacting it is the thrower's job, not this one's.
 */
function logSafeDetailOf(e: Err): string | undefined {
  if (e === null || typeof e !== 'object') return undefined
  const detail = e.logSafeDetail
  return typeof detail === 'string' && detail !== '' ? detail : undefined
}

/**
 * Rollbar's default grouping (exception class and frames) put plain Errors from different
 * routes into one item, since each throws from Nitro's `Object.handler`. Group on context
 * and message instead, with per-request text (urls, quoted values, ids) normalised so it
 * doesn't open an item each. Contexts get the same treatment: some interpolate paths,
 * messages or request bodies.
 * The Node notifier lifts a top-level custom `fingerprint` onto the payload (the browser
 * one leaves it in `custom`, so client reports group as before); a caller's own wins.
 */
const normaliseForGrouping = (text: string): string =>
  text
    .slice(0, 300)
    .replace(/\bhttps?:\/\/\S+/g, 'url')
    .replace(/"[^"]*"/g, '""')
    .replace(/[0-9a-f-]{8,}/gi, 'x')
    .replace(/\b(?=[A-Za-z_-]*\d)[A-Za-z0-9_-]{12,}\b/g, 'x')
    .replace(/\/\d+(?=\/|\b)/g, '/0')
    // Short numbers stay: HTTP statuses and Mysoft Kode values are the cause, not an id.
    .replace(/\d{4,}/g, '0')

export const fingerprintOf = (context: string, errors: string): string =>
  `${normaliseForGrouping(context)} | ${normaliseForGrouping(errors)}`

// The Node notifier takes an argument with any of these keys for the request and drops
// the rest of it, fingerprint included.
const ROLLBAR_REQUEST_KEYS = ['headers', 'protocol', 'url', 'method', 'body', 'route']

const keepOutOfRequest = (payload: object): object => {
  const entries = Object.entries(payload)
  const requestFields = entries.filter(([k]) => ROLLBAR_REQUEST_KEYS.includes(k))
  if (requestFields.length === 0) return payload
  return {
    ...Object.fromEntries(entries.filter(([k]) => !ROLLBAR_REQUEST_KEYS.includes(k))),
    requestFields: Object.fromEntries(requestFields),
  }
}

export const flagError = (
  e: Err,
  context: string,
  externalLogger?: ErrorSink,
  payload: object | '' = '',
): { message: string; errors: string } => {
  const sink = resolveSink(externalLogger)
  const errorMessage = e?.message || ''
  const message = `Error in [${context}]`
  const errors =
    e instanceof ZodError
      ? superjson.stringify(e.flatten())
      : e instanceof Error
        ? e.message
        : typeof e === 'string'
          ? e
          : describeUnknown(e)
  const detail = logSafeDetailOf(e)
  const printablePayload =
    typeof payload === 'string' ? payload : ' -- payload: ' + superjson.stringify(payload)
  const printableDetail = detail ? ' -- detail: ' + detail : ''
  console.error(message + ' -- ' + errorMessage + printablePayload + printableDetail, errors)

  // Rollbar only extracts a stack trace when given an Error instance; passing a string drops it.
  const errorArg: Error = e instanceof Error ? e : new Error(errors)
  const basePayload = payload && typeof payload === 'object' ? keepOutOfRequest(payload) : {}
  // A ZodError's text is quoted JSON, which would normalise away; its fields tell it apart.
  const groupingText =
    e instanceof ZodError ? `ZodError ${Object.keys(e.flatten().fieldErrors).sort().join(',')}` : errors
  const customPayload = {
    fingerprint: fingerprintOf(context, groupingText),
    ...basePayload,
    ...(detail && { logSafeDetail: detail }),
    ...(e instanceof ZodError && { zodDetails: errors }),
    // describeUnknown trims to a groupable title; keep the whole object here.
    ...(e !== null &&
      typeof e === 'object' &&
      !(e instanceof Error) && { errorData: stringifySafely(e) ?? '[unserialisable]' }),
  }
  sink?.error(message, errorArg, customPayload)
  return { message, errors }
}

/**
 * Rollbar queues items and ships them over HTTP in the background, so a
 * process that exits on the next line drops whatever is still in flight.
 * Short-lived processes (the cron CLI) must await this before `process.exit`
 * or their terminal success/failure item never leaves the box.
 *
 * Resolves on the timeout as well as on drain: a reporter that never calls
 * back must not wedge the exit path.
 */
export const flushErrorReporter = (
  externalLogger: Rollbar | null = rollbar(),
  timeoutMs = 5000,
): Promise<void> => {
  if (!externalLogger?.wait) return Promise.resolve()
  return new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, timeoutMs)
    // Never let this hold the process open on its own — the caller is usually
    // about to exit, and the timeout is a backstop, not work.
    timer.unref?.()
    const done = () => {
      clearTimeout(timer)
      resolve()
    }
    try {
      externalLogger.wait(done)
    } catch {
      done()
    }
  })
}

export const flagLog = (
  message: any,
  externalLogger?: ErrorSink,
  payload: object | '' = '',
): void => {
  const sink = resolveSink(externalLogger)
  if (sink?.info) {
    sink.info(message, payload)
  } else {
    sink?.log(message, payload)
  }
  console.log(message, payload)
}

/**
 * Between flagLog (routine, info-level) and flagError (an application bug):
 * for signals that are working as designed but still worth a human noticing —
 * a blocked submission, a policy rejection — without the noise of error-level
 * alerting or the stack-trace framing flagError builds around an Error object.
 */
export const flagWarn = (
  message: any,
  externalLogger?: ErrorSink,
  payload: object | '' = '',
): void => {
  const sink = resolveSink(externalLogger)
  if (sink?.warning) {
    sink.warning(message, payload)
  } else {
    sink?.log(message, payload)
  }
  console.warn(message, payload)
}
